import React from 'react'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import { useDispatch } from 'react-redux'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useNavigation } from '@react-navigation/core'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@components/base'

import { SkipButton } from '@src/screens/SignUp/components/SkipButton'
import { NavigationContainer } from '@src/screens/Common/containers'
import { useAllowSkipPurchaseFlow, useShouldShowPurchaseFlow } from '@src/utils/hooks'
import { InlineAlert } from '@src/components'
import { UiInlineAlert } from '@src/types'
import { useLazyScreenPreload } from '@src/navigation/utils'
import {
  AppStackParamList,
  CommonAuthorizedStackParamList,
  OnboardingStackParamsList,
} from '@src/navigation/types'

export const ProgramError = () => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const allowSkipPurchaseFlow = useAllowSkipPurchaseFlow()
  const showPurchaseFlow = useShouldShowPurchaseFlow()

  const appNavigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const authorizedNavigation = useNavigation<StackNavigationProp<CommonAuthorizedStackParamList>>()
  const onboardingNavigation = useNavigation<StackNavigationProp<OnboardingStackParamsList>>()

  const dispatch = useDispatch()

  const onSkipPress = () => {
    dispatch({
      type: 'app/skipOnboardingFlow',
    })
  }

  const onSignOutPress = () => {
    dispatch({ type: 'app/logout' })
  }

  useLazyScreenPreload('SupportHub')

  const onContactSupportPress = () => {
    authorizedNavigation.navigate('SupportHub')
  }

  const onChooseNewProgramPress = () => {
    if (showPurchaseFlow) {
      return onboardingNavigation.navigate('OnboardingSubscriptionOptions')
    }

    appNavigation.replace('Subscriptions')
  }

  return (
    <NavigationContainer title="Not Eligible" style={styles.container}>
      <ScrollView
        style={styles.container}
        bounces={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 16,
          paddingBottom: showPurchaseFlow ? Math.max(insets.bottom, 16) : insets.bottom + 16,
        }}
      >
        <View style={styles.content}>
          <Icon name="warning" weight="fill" style={styles.icon} />
          <Text type="title-3" style={styles.description}>
            It seems like you can’t move forward with your program choice. Please select a different
            program.
          </Text>
        </View>
        <InlineAlert
          category={UiInlineAlert.Neutral}
          message={
            <Text type="regular">
              Questions? Don't hesitate to reach out to our{' '}
              <TouchableOpacity
                accessibilityLabel="Contact Support"
                onPress={onContactSupportPress}
              >
                <Text type="regular" bold lineSpacing="none" style={styles.supportLink}>
                  Support Team
                </Text>
              </TouchableOpacity>
              .
            </Text>
          }
        />
        <View style={styles.buttonsContainer}>
          <Button
            type="primary"
            size="block"
            onPress={onChooseNewProgramPress}
            accessibilityLabel="Choose New Program"
          >
            Choose New Program
          </Button>
          {showPurchaseFlow && (
            <Button
              type="transparent"
              size="block"
              onPress={onSignOutPress}
              accessibilityLabel="Sign Out"
            >
              Sign Out
            </Button>
          )}
          {showPurchaseFlow && allowSkipPurchaseFlow && (
            <SkipButton title="Skip checkout" onPress={onSkipPress} />
          )}
        </View>
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  content: {
    flexGrow: 1,
    alignItems: 'center',
  },
  description: {
    marginTop: 32,
    textAlign: 'center',
    maxWidth: 330,
  },
  supportLink: {
    color: 'theme.text.link',
  },
  icon: {
    marginTop: 56,
    width: 56,
    height: 56,
    color: 'theme.error.base',
  },
  buttonsContainer: {
    marginTop: 16,
    gap: 8,
  },
})
